import type { EntryFull } from '@siteday/shared';
import { useMemo, useState } from 'react';

import { errorMessage } from '@/api/client';
import { Pill } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Radio } from '@/components/ui/toggle';
import { useToast } from '@/components/ui/toast';
import { cn } from '@/lib/cn';
import { fmtWhen } from '@/lib/dates';
import type { Snapshot } from '@/lib/diff';

import {
  buildMergedDocument,
  conflictSides,
  defaultPicks,
  SECTIONS,
  sectionsDiffer,
  snapshotOfRevision,
  summarizeSections,
  type Picks,
  type Side,
} from './conflict';
import { useResolveConflict } from './useEntries';

/** W4 needs_review: office | field side by side, per-section keep radios, Keep-all shortcuts, Resolve, Edit merged. */
export function ConflictView({
  entry,
  tz,
  onEditMerged,
}: {
  entry: EntryFull;
  tz: string;
  onEditMerged: (seed: Snapshot | null) => void;
}) {
  const { office, field } = useMemo(() => conflictSides(entry.revisions), [entry.revisions]);
  const officeSnap = useMemo(() => snapshotOfRevision(office), [office]);
  const fieldSnap = useMemo(() => snapshotOfRevision(field), [field]);
  const differs = useMemo(() => sectionsDiffer(officeSnap, fieldSnap), [officeSnap, fieldSnap]);
  const [picks, setPicks] = useState<Picks>(() => defaultPicks(differs));
  const resolve = useResolveConflict(entry.id);
  const toast = useToast();
  const officeText = summarizeSections(officeSnap);
  const fieldText = summarizeSections(fieldSnap);

  const setAll = (side: Side) =>
    setPicks(Object.fromEntries(SECTIONS.map((s) => [s.key, side])) as Picks);
  const allOffice = SECTIONS.every((s) => picks[s.key] === 'office');
  const allField = SECTIONS.every((s) => picks[s.key] === 'field');

  const submit = () => {
    const body = allOffice
      ? { choose: 'office' as const }
      : allField
        ? { choose: 'field' as const }
        : {
            choose: 'merged' as const,
            merged: buildMergedDocument(entry, officeSnap, fieldSnap, picks),
          };
    resolve.mutate(body, {
      onSuccess: () =>
        toast.success('Resolved — the phone is unlocked and the PDF will regenerate'),
      onError: (e) => toast.error(errorMessage(e)),
    });
  };

  return (
    <>
      <div className="mb-3.5 flex flex-wrap items-start gap-3">
        <p className="m-0 max-w-[80ch] text-ink2">
          {office?.changedByName ?? 'The office'} edited this day{' '}
          {office ? fmtWhen(office.changedAt, tz).toLowerCase() : ''}. {entry.foremanName}'s phone
          pushed a newer field version {field ? fmtWhen(field.changedAt, tz).toLowerCase() : ''}.
          Choose which version stands for each section. Both stay in revision history.
        </p>
        <div className="ml-auto flex gap-2">
          <Button onClick={() => onEditMerged(fieldSnap)}>Edit merged</Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={resolve.isPending}
            data-testid="resolve"
          >
            Resolve with selections
          </Button>
        </div>
      </div>
      <div className="cmp">
        <Column
          side="office"
          title={
            <>
              <Pill tone="info">Office version</Pill> v{office?.version ?? '—'} ·{' '}
              {office?.changedByName ?? '—'} · {office ? fmtWhen(office.changedAt, tz) : ''}
            </>
          }
          text={officeText}
          differs={differs}
          picks={picks}
          onPick={(k) => setPicks((p) => ({ ...p, [k]: 'office' }))}
          onAll={() => setAll('office')}
        />
        <Column
          side="field"
          title={
            <>
              <Pill tone="orange">Field version</Pill> v{field?.version ?? '—'} ·{' '}
              {field?.changedByName ?? entry.foremanName} ·{' '}
              {field ? fmtWhen(field.changedAt, tz) : ''} from phone
            </>
          }
          text={fieldText}
          differs={differs}
          picks={picks}
          onPick={(k) => setPicks((p) => ({ ...p, [k]: 'field' }))}
          onAll={() => setAll('field')}
        />
      </div>
    </>
  );
}

function Column({
  side,
  title,
  text,
  differs,
  picks,
  onPick,
  onAll,
}: {
  side: Side;
  title: React.ReactNode;
  text: ReturnType<typeof summarizeSections>;
  differs: Record<string, boolean>;
  picks: Picks;
  onPick: (k: keyof Picks) => void;
  onAll: () => void;
}) {
  return (
    <div className="cmp-col" data-side={side}>
      <div className="flex flex-wrap items-center gap-2 border-b border-line2 bg-surface2 px-3.5 py-2.5 font-bold">
        {title}
        <Button size="sm" className="ml-auto" onClick={onAll}>
          Keep all {side}
        </Button>
      </div>
      {SECTIONS.map((s) => (
        <div key={s.key} className={cn('sec', differs[s.key] && 'chg')}>
          <div className="eyebrow flex items-center gap-2">
            {s.label}
            {differs[s.key] || s.key !== 'photos' ? (
              <Radio
                name={`pick-${s.key}`}
                checked={picks[s.key] === side}
                onChange={() => onPick(s.key)}
                label={`keep ${side}`}
                className="ml-auto"
              />
            ) : null}
          </div>
          <div className="mt-1 text-[13px]">
            {text[s.key].map((line, i) => (
              <div key={i} className={cn(i === 0 && s.key !== 'notes' && 'font-semibold', 'num')}>
                {line}
                {!differs[s.key] && i === 0 && s.key !== 'notes' ? (
                  <span className="text-ink3"> — same in both</span>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
